import Form from "../../Helpers/Form/Form";
import { Offcanvas } from "react-bootstrap";
import FormHeader from "../../Helpers/Form/FormParts/FormHeader";
import trans, { isRTL } from "../../../utils";
import CallCenterFormBody from "./CallCenterFormBody";
import { useCreateCallCenterRequestMutation } from "../../../Redux/service/CallCenter/CallCenter";
import { Formik, Form as FormFormik, FieldArray } from "formik";
import Button from "../../Helpers/Button/Button";
import * as Yup from "yup";
import dayjs from "dayjs";
import { useContext } from "react";
import { popupcontext } from "../../../context/Popupscontext";
import { toast } from "react-toastify";
import {
	isValidEgyptianPhone,
	COMPLAINT_DESCRIPTION_MAX_LENGTH,
	MAX_SECONDARY_PHONES,
} from "./callCenterValidation";

const initialValues = {
	mobileNumber: "",
	customerFound: null, // null | true | false — drives which sections are locked
	customerName: "",
	state: "",
	city: "",
	villageId: "",
	address: "",
	landline: "",
	secondaryPhones: [],
	matchedPhoneNumber: null, // set by the phone lookup; highlights which saved number was called from
	latitude: "",
	longitude: "",
	productId: "",
	modelId: "",
	defects: [],
	sparePartIds: [],
	complaintDescription: "",
	preferredVisitTime: "",
	preferredVisitTimeEnd: "",
	callBeforePerforming: false,
	color: "",
};

const validationSchema = Yup.object({
	mobileNumber: Yup.string()
		.required(trans("Mobile number is required"))
		.test(
			"valid-egyptian-phone",
			trans("Enter a valid Egyptian mobile or landline number"),
			isValidEgyptianPhone,
		),
	productId: Yup.string().required(trans("Product is required")),
	complaintDescription: Yup.string()
		.required(trans("Complaint description is required"))
		.min(10, trans("Complaint description cannot minimum 10 characters"))
		.max(
			COMPLAINT_DESCRIPTION_MAX_LENGTH,
			trans(
				`Complaint description cannot exceed ${COMPLAINT_DESCRIPTION_MAX_LENGTH} characters`,
			),
		),
	customerName: Yup.string().when("customerFound", {
		is: false,
		then: (schema) => schema.required(trans("Customer name is required")),
	}),
	state: Yup.string().when("customerFound", {
		is: false,
		then: (schema) => schema.required(trans("Governorate is required")),
	}),
	city: Yup.string().when("customerFound", {
		is: false,
		then: (schema) => schema.required(trans("Markaz/Qism is required")),
	}),
	villageId: Yup.string().when("customerFound", {
		is: false,
		then: (schema) => schema.required(trans("Village is required")),
	}),
	address: Yup.string().when("customerFound", {
		is: false,
		then: (schema) => schema.required(trans("Address is required")),
	}),
	latitude: Yup.mixed().when("customerFound", {
		is: false,
		then: (schema) => schema.required(trans("Please select the customer location on the map")),
	}),
	longitude: Yup.mixed().when("customerFound", {
		is: false,
		then: (schema) => schema.required(trans("Please select the customer location on the map")),
	}),
	preferredVisitTime: Yup.string().test(
		"future-visit-time",
		trans("Preferred visit time must be in the future"),
		(value) =>
			!value || dayjs(value, "YYYY-MM-DD HH:mm:ss").isAfter(dayjs()),
	),
	secondaryPhones: Yup.array().of(
		Yup.string()
			.required(trans("Phone number is required"))
			.test(
				"valid-egyptian-phone",
				trans("Enter a valid Egyptian mobile or landline number"),
				isValidEgyptianPhone,
			)
	).max(
		MAX_SECONDARY_PHONES,
		trans(`You can add up to ${MAX_SECONDARY_PHONES} additional phone numbers`),
	).test(
		"unique-phones",
		localStorage.getItem("lang") === "ar" ? "يجب أن تكون أرقام الهواتف الإضافية فريدة" : "Secondary phone numbers must be unique",
		function(phones) {
			if (!Array.isArray(phones)) return true;
			const normalizedPhones = phones
				.filter(p => p && typeof p === 'string')
				.map(p => p.replace(/\D/g, "")); 
			const uniquePhones = new Set(normalizedPhones);
			return uniquePhones.size === normalizedPhones.length;
		}
	).test(
		"no-duplicate-main",
		localStorage.getItem("lang") === "ar" ? "لا يمكن أن تكون أرقام الهواتف الإضافية هي نفس رقم الهاتف المحمول الرئيسي" : "Secondary phone numbers cannot be the same as the main mobile number",
		function(phones) {
			if (!Array.isArray(phones) || !this.parent.mobileNumber) return true;
			const normalizedMain = this.parent.mobileNumber.replace(/\D/g, "");
			const normalizedSecondary = phones
				.filter(p => p && typeof p === 'string')
				.map(p => p.replace(/\D/g, ""));
			return !normalizedSecondary.includes(normalizedMain);
		}
	),
	defects: Yup.array(),
	sparePartIds: Yup.array(),
	callBeforePerforming: Yup.boolean(),
});

export default function FormCallCenterRequest({ createForm, setCreateForm }) {
	const { setEdited } = useContext(popupcontext);
	const [createCallCenterRequest] = useCreateCallCenterRequestMutation();

	const handleFormSubmit = async (values, setSubmitting, resetForm) => {
		setSubmitting(true);

		const payload = {
			mobileNumber: values.mobileNumber,
			// Which saved number the customer actually called from — may differ
			// from mobileNumber (the one searched/entered above) if the agent
			// explicitly picked a different saved number in the Contact Number
			// panel. Falls back to the primary itself when nothing was selected.
			callerPhoneNumber: values.matchedPhoneNumber || values.mobileNumber || undefined,
			customerName: values.customerName || undefined,
			state: values.state || undefined,
			city: values.city || undefined,
			villageId: values.villageId || undefined,
			address: values.address || undefined,
			landline: values.landline || undefined,
			secondaryPhones: values.secondaryPhones?.length ? values.secondaryPhones : undefined,
			latitude: values.latitude !== "" ? Number(values.latitude) : undefined,
			longitude: values.longitude !== "" ? Number(values.longitude) : undefined,
			productId: values.productId,
			modelId: values.modelId || undefined,
			defects: values.defects?.length ? values.defects : undefined,
			sparePartIds: values.sparePartIds?.length ? values.sparePartIds : undefined,
			complaintDescription: values.complaintDescription,
			preferredVisitTime: values.preferredVisitTime || undefined,
			preferredVisitTimeEnd: values.preferredVisitTimeEnd || undefined,
			callBeforePerforming: values.callBeforePerforming,
			color: values.color || undefined,
		};

		try {
			const result = await createCallCenterRequest(payload).unwrap();
			toast.success(
				`${trans("Service Request Created Successfully")}${
					result?.data?.code ? ` - ${result.data.code}` : ""
				}`,
				{
					autoClose: 1500,
					rtl: localStorage.getItem("lang") === "ar" ? true : false,
				},
			);
			resetForm();
			setCreateForm(false);
			setEdited(true);
		} catch (err) {
			const errorMsg = trans(err?.data?.message ?? "Something went wrong");
			toast.error(errorMsg, {
				autoClose: 1500,
				rtl: localStorage.getItem("lang") === "ar" ? true : false,
			});
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Form show={createForm}>
			<Offcanvas.Header>
				<FormHeader
					title={trans("Create New Request")}
					onClose={() => setCreateForm(false)}
				/>
			</Offcanvas.Header>
			<Offcanvas.Body>
				{createForm && (
					<Formik
						initialValues={initialValues}
						onSubmit={(values, { setSubmitting, resetForm }) =>
							handleFormSubmit(values, setSubmitting, resetForm)
						}
						validationSchema={validationSchema}
					>
						{({ handleSubmit, isSubmitting }) => (
							<FormFormik onSubmit={handleSubmit} dir={!isRTL ? "ltr" : "rtl"}>
								<CallCenterFormBody />
								<div className="offcanvas-footer m-0">
									<div className="offcanvas-footer-body gap-8">
										<Button
											type={"primary"}
											htmlType="submit"
											loading={isSubmitting}
											disabled={isSubmitting}
										>
											{isSubmitting ? trans("Saving...") : trans("Submit")}
										</Button>
										<Button
											type={"default"}
											htmlType="button"
											disabled={isSubmitting}
											onClick={() => setCreateForm(false)}
										>
											{trans("Cancel")}
										</Button>
									</div>
								</div>
							</FormFormik>
						)}
					</Formik>
				)}
			</Offcanvas.Body>
		</Form>
	);
}
